/**
 * OnboardingTourMediador (PR #100) — tour específico pro painel Mediador.
 *
 * Auto-displays na primeira visita ao /admin/curadoria (ou qualquer rota
 * de mediador). Usa STORAGE_KEY próprio (não interfere com tour Cliente).
 *
 * 6 steps cobrindo:
 * 1. Bem-vindo (fila + SLA + role)
 * 2. Atalhos teclado (J/K navegar, Space rapid action, ESC fechar)
 * 3. Filtros avançados (vertical/etapa/SLA)
 * 4. Ações em lote (encerrar múltiplos casos)
 * 5. Pulso (produtividade do dia)
 * 6. Heatmap SLA (priorizar vertical com mais breach)
 */
import { useState, useEffect } from "react";
import {
  X,
  ChevronRight,
  ChevronLeft,
  ClipboardCheck,
  Keyboard,
  Filter,
  CheckCheck,
  Activity,
  Flame,
} from "lucide-react";

const STORAGE_KEY = "acordei_onboarding_mediador_done_v1";

const STEPS = [
  {
    icon: ClipboardCheck,
    color: "#a78bfa",
    title: "Bem-vindo ao painel Mediador",
    subtitle: "Curadoria humana de casos",
    content:
      "Aqui você revisa casos da fila, aplica ações em lote, fecha submissões manualmente e monitora produtividade. Toda ação é registrada no audit log.",
    highlight:
      "Sua função: curadoria + qualidade do output. Casos automáticos vão direto pro fluxo; aqui só os que precisam intervenção humana.",
  },
  {
    icon: Keyboard,
    color: "#22d3ee",
    title: "Atalhos de teclado",
    subtitle: "Triagem mais rápida",
    content:
      "• J / K → navegar casos na lista\n• Space → ação rápida no caso selecionado\n• E → encerrar caso (manual)\n• Shift+A → selecionar todos\n• ESC → fechar modal\n• ? → mostrar atalhos completos",
    highlight: "Foco visual ring violeta indica caso ativo.",
  },
  {
    icon: Filter,
    color: "#fbbf24",
    title: "Filtros avançados",
    subtitle: "Foque no que importa",
    content:
      "Combine filtros por:\n• Vertical (saúde, telecom, bancário…)\n• Etapa (aguardando resposta, aguardando evidência…)\n• SLA (vencido / próximo do vencimento / OK)\n• Período de criação",
    highlight: "Filtros persistem na URL — compartilhe links com colegas.",
  },
  {
    icon: CheckCheck,
    color: "#4ade80",
    title: "Ações em lote",
    subtitle: "Processe vários de uma vez",
    content:
      "Selecione múltiplos casos (Shift+A pra todos) e aplique:\n• Encerrar como frustrada_arquivada\n• Marcar pra revisão\n• Atribuir a outro mediador\n• Mover de etapa",
    highlight: "Audit log registra cada ação em lote com seu nome + timestamp.",
  },
  {
    icon: Activity,
    color: "#fb923c",
    title: "Pulso operacional",
    subtitle: "Seu dia em números",
    content:
      "Card no topo mostra:\n• Casos atendidos hoje (você)\n• Mediana de tempo por caso\n• Taxa de acordo da última semana\n• Ranking individual vs equipe",
    highlight: "Atualizado em tempo real conforme você fecha casos.",
  },
  {
    icon: Flame,
    color: "#f472b6",
    title: "Heatmap SLA por vertical",
    subtitle: "Priorize verticais quentes",
    content:
      "Card no painel mostra % de SLA breach por vertical. Verticais vermelhas têm mais casos vencendo → priorize-as. Verticais verdes estão dentro do prazo.",
    highlight: "Use junto com filtros (etapa=aguardando_resposta + vertical=saude) pra fila focada.",
  },
];

export default function OnboardingTourMediador() {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    // Só mostra se: (a) não foi visto antes E (b) está em rota de mediador
    const done = localStorage.getItem(STORAGE_KEY);
    if (!done && window.location.pathname.startsWith("/admin/curadoria")) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (!visible) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, step]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
  };

  const next = () => {
    if (step < STEPS.length - 1) setStep((s) => s + 1);
    else dismiss();
  };

  const prev = () => setStep((s) => Math.max(0, s - 1));

  if (!visible) return null;

  const current = STEPS[step];
  const Icon = current.icon;
  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-mod-title"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.75)",
        backdropFilter: "blur(4px)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={(e) => e.target === e.currentTarget && dismiss()}
    >
      <div
        style={{
          background: "#0f172a",
          border: "1px solid #1e293b",
          borderRadius: "20px",
          width: "100%",
          maxWidth: "480px",
          overflow: "hidden",
          boxShadow: "0 25px 60px rgba(0,0,0,0.5)",
        }}
      >
        <div style={{ height: "3px", background: "#1e293b" }}>
          <div
            style={{
              height: "100%",
              width: `${progress}%`,
              background: `linear-gradient(90deg, ${current.color}, ${current.color}99)`,
              transition: "width 0.4s cubic-bezier(0.23, 1, 0.32, 1)",
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 24px 0",
          }}
        >
          <span style={{ fontSize: "12px", color: "#475569", fontWeight: 600 }}>
            <span style={{ color: "#a78bfa" }}>Mediador</span> · {step + 1} de {STEPS.length}
          </span>
          <button
            onClick={dismiss}
            aria-label="Fechar tour"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#475569",
              padding: "4px",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: "16px 24px 24px" }}>
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              background: `${current.color}20`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "16px",
            }}
          >
            <Icon size={24} color={current.color} />
          </div>

          <h2
            id="onboarding-mod-title"
            style={{
              fontSize: "20px",
              fontWeight: 700,
              color: "#f1f5f9",
              margin: "0 0 4px",
            }}
          >
            {current.title}
          </h2>
          <p style={{ fontSize: "13px", color: "#64748b", margin: "0 0 16px" }}>
            {current.subtitle}
          </p>

          <p
            style={{
              fontSize: "14px",
              color: "#cbd5e1",
              lineHeight: 1.6,
              margin: "0 0 16px",
              whiteSpace: "pre-line",
            }}
          >
            {current.content}
          </p>

          {current.highlight && (
            <div
              style={{
                padding: "12px 14px",
                borderRadius: "10px",
                background: `${current.color}10`,
                border: `1px solid ${current.color}30`,
                fontSize: "13px",
                color: current.color,
                fontWeight: 600,
              }}
            >
              {current.highlight}
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 24px",
            borderTop: "1px solid #1e293b",
            background: "#020617",
          }}
        >
          <button
            onClick={prev}
            disabled={step === 0}
            style={{
              background: "none",
              border: "none",
              color: step === 0 ? "#1e293b" : "#94a3b8",
              cursor: step === 0 ? "not-allowed" : "pointer",
              padding: "8px 12px",
              fontSize: "14px",
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <ChevronLeft size={16} /> Voltar
          </button>

          <button
            onClick={next}
            style={{
              background: `linear-gradient(135deg, ${current.color}, ${current.color}dd)`,
              border: "none",
              color: "#0f172a",
              cursor: "pointer",
              padding: "10px 18px",
              fontSize: "14px",
              fontWeight: 700,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            {step === STEPS.length - 1 ? "Começar" : "Próximo"}
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
